import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import { 
  FolderOpen, FileText, Download, Eye, X, 
  ChevronRight, ChevronDown, User, Upload,
  File, Image, FileArchive, Loader2
} from 'lucide-react';

export default function EmployeeFiles({ employee, onClose }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [viewingFile, setViewingFile] = useState(null);
  const [uploadingTo, setUploadingTo] = useState(null);

  useEffect(() => {
    if (employee) {
      fetchEmployeeFiles();
    }
  }, [employee]);

  const fetchEmployeeFiles = async () => {
    setLoading(true);
    try {
      // Try to find employee folder by name
      const nameParts = employee.full_name.split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts[nameParts.length - 1];
      
      // Check both employees and New_Hires folders
      const allFolders = await api.get('/storage/employee-files');
      
      // Find folder matching this employee
      const matchingFolder = allFolders.data.find(folder => {
        const folderName = folder.name.toLowerCase();
        return folderName.includes(lastName.toLowerCase()) && 
               folderName.includes(firstName.toLowerCase());
      });

      if (matchingFolder) {
        const response = await api.get(`/storage/employee-files/${matchingFolder.path}`);
        setFiles(response.data);
      } else {
        setFiles({ name: 'No folder found', path: '', files: [], subfolders: [] });
      }
    } catch (error) {
      console.error('Failed to load employee files:', error);
      toast.error('Failed to load employee files');
      setFiles({ name: 'Error', path: '', files: [], subfolders: [] });
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return <Image className="w-4 h-4 text-green-600" />;
    if (['pdf'].includes(ext)) return <FileText className="w-4 h-4 text-red-600" />;
    if (['zip', 'rar', '7z'].includes(ext)) return <FileArchive className="w-4 h-4 text-yellow-600" />;
    return <File className="w-4 h-4 text-blue-600" />;
  };

  const viewFile = async (filePath) => {
    try {
      const response = await api.get(`/storage/employee-file-url?path=${encodeURIComponent(filePath)}`);
      setViewingFile({ path: filePath, url: response.data.url });
    } catch (error) {
      console.error('Failed to get file URL:', error);
      toast.error('Failed to open file');
    }
  };

  const downloadFile = async (filePath, fileName) => {
    try {
      const response = await api.get(`/storage/employee-file-url?path=${encodeURIComponent(filePath)}`);
      const link = document.createElement('a');
      link.href = response.data.url;
      link.download = fileName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Failed to download file:', error);
      toast.error('Failed to download file');
    }
  };

  const toggleFolder = (path) => {
    setExpandedFolders(prev => ({
      ...prev,
      [path]: !prev[path]
    }));
  };

  const handleFileUpload = async (e, folderPath) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingTo(folderPath);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder_path', folderPath);

      await api.post('/storage/employee-files/upload-form', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('File uploaded successfully');
      fetchEmployeeFiles();
    } catch (error) {
      console.error('Failed to upload file:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploadingTo(null);
    }
  };

  const renderFiles = (fileList, path = '') => {
    if (!fileList || fileList.length === 0) {
      return <p className="text-sm text-gray-500 italic py-2 px-4">No files</p>;
    }

    return fileList.map((file, index) => (
      <div 
        key={`${path}-${file.name}-${index}`}
        className="flex items-center justify-between py-2 px-4 hover:bg-purple-50 border-b border-gray-100 last:border-0"
      >
        <div className="flex items-center gap-3">
          {getFileIcon(file.name)}
          <div>
            <p className="text-sm font-medium text-gray-900">{file.name}</p>
            {file.size && (
              <p className="text-xs text-gray-500">
                {(file.size / 1024).toFixed(1)} KB
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => viewFile(file.path)}
            className="text-purple-600 hover:text-purple-800 hover:bg-purple-100"
          >
            <Eye className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => downloadFile(file.path, file.name)}
            className="text-gray-600 hover:text-gray-800 hover:bg-gray-100"
          >
            <Download className="w-4 h-4" />
          </Button>
        </div>
      </div>
    ));
  };

  const renderFolder = (folder, depth = 0) => {
    const isExpanded = expandedFolders[folder.path] !== false; // Default to expanded
    
    return (
      <div key={folder.path} className={depth > 0 ? 'ml-4' : ''}>
        <div 
          className="flex items-center justify-between py-2 px-3 bg-purple-50 rounded-lg mb-1 cursor-pointer hover:bg-purple-100"
          onClick={() => toggleFolder(folder.path)}
        >
          <div className="flex items-center gap-2">
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-purple-600" />
            ) : (
              <ChevronRight className="w-4 h-4 text-purple-600" />
            )}
            <FolderOpen className="w-4 h-4 text-purple-600" />
            <span className="font-medium text-purple-900">{folder.name}</span>
            <span className="text-xs text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full">
              {folder.files?.length || 0} files
            </span>
          </div>
        </div>
        
        {isExpanded && (
          <div className="border border-purple-100 rounded-lg overflow-hidden mb-2">
            {renderFiles(folder.files, folder.path)}
            {folder.subfolders?.map(sub => renderFolder(sub, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (!employee) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="employee-files-modal">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[85vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-full">
                <User className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-purple-900" data-testid="employee-files-name">{employee.full_name}</h3>
                <p className="text-sm text-gray-600">{employee.email} • {employee.role}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg"
              data-testid="employee-files-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-purple-600" />
              Employee Files
            </h4>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
            </div>
          ) : files.files?.length === 0 && files.subfolders?.length === 0 ? (
            <div className="text-center py-12">
              <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No files found for this employee</p>
              <p className="text-sm text-gray-400 mt-1">Files will appear here when uploaded</p>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Root files */}
              {files.files?.length > 0 && (
                <div className="border border-purple-100 rounded-lg overflow-hidden mb-2">
                  {renderFiles(files.files, files.path)}
                </div>
              )}
              
              {/* Subfolders */}
              {files.subfolders?.map(folder => renderFolder(folder))}
            </div>
          )}
        </div>

        {/* File Viewer Modal */}
        {viewingFile && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-60">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <p className="font-medium text-gray-900">{viewingFile.path.split('/').pop()}</p>
                <button
                  onClick={() => setViewingFile(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 max-h-[80vh] overflow-auto">
                {viewingFile.path.toLowerCase().endsWith('.pdf') ? (
                  <iframe 
                    src={viewingFile.url} 
                    className="w-full h-[70vh]"
                    title="PDF Viewer"
                  />
                ) : viewingFile.path.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                  <img 
                    src={viewingFile.url} 
                    alt="File preview" 
                    className="max-w-full h-auto mx-auto"
                  />
                ) : (
                  <div className="text-center py-8">
                    <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">Preview not available for this file type</p>
                    <Button
                      onClick={() => window.open(viewingFile.url, '_blank')}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download File
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
